import React, { Component } from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { Table, Icon, Popup } from "semantic-ui-react";
import Moment from "moment";
import PhotoEditingJob from "~/models/PhotoEditing/PhotoEditingJob";
import JobStatus from "~/constants/PhotoEditing/JobStatus";
import EstimatedTime from "./EstimatedTime";
import JobType from "~/constants/PhotoEditing/JobType";
import HdRealImagesPerProduct from "~/constants/PhotoEditing/HdRealImagesPerProduct";

@observer
class JobSummary extends Component {
  getStatusIconName(status) {
    switch (status) {
      case JobStatus.IN_CREATION:
        return "edit";
      case JobStatus.IN_PROGRESS:
        return "settings";
      case JobStatus.DONE:
        return "flag checkered";
      default:
        throw new Error(`unexpected status ${status}`);
    }
  }

  getCreditCostText(cost) {
    if (cost === 0) {
      return "FREE";
    }

    const creditText = cost > 1 ? "credits" : "credit";

    return `${cost} ${creditText}`;
  }

  render() {
    const { photoEditingJob, costPerItem, totalCost, showProviderInfo } = this.props;

    const productBreakDown = [];
    let tableRowKeyId = 11;

    if (photoEditingJob.typeId === JobType.HD_REAL) {
      productBreakDown.push(
        <Table.Row key={tableRowKeyId}>
          <Table.Cell>
            Cost per {HdRealImagesPerProduct} image{HdRealImagesPerProduct > 1 ? "s" : null}
          </Table.Cell>
          <Table.Cell>{this.getCreditCostText(costPerItem)}</Table.Cell>
        </Table.Row>,
      );

      tableRowKeyId += 1;
    } else if (photoEditingJob.typeId === JobType.BASIC_HDR && photoEditingJob.refunds.length > 0) {
      // eslint-disable-next-line array-callback-return
      photoEditingJob.refunds.map((imagePackage, index) => {
        let imagePackageCost = this.getCreditCostText(costPerItem);

        if (imagePackage.isFree) {
          imagePackageCost = "FREE";
        } else if (imagePackage.refundDts !== null) {
          imagePackageCost = "REFUNDED";
        }

        productBreakDown.push(
          <Table.Row key={tableRowKeyId}>
            <Table.Cell>
              Image Package #{50 * index + 1}-{50 * (index + 1)}
            </Table.Cell>
            <Table.Cell>{imagePackageCost}</Table.Cell>
          </Table.Row>,
        );

        tableRowKeyId += 1;
      });
    }

    return (
      <Table>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Summary</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">
              <Popup
                content={photoEditingJob.statusLabel}
                trigger={<Icon name={this.getStatusIconName(photoEditingJob.status)} />}
              />
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {showProviderInfo ? (
            <Table.Row key={1}>
              <Table.Cell>Provider ID</Table.Cell>
              <Table.Cell>{photoEditingJob.provider.providerId}</Table.Cell>
            </Table.Row>
          ) : null}
          {showProviderInfo ? (
            <Table.Row key={2}>
              <Table.Cell>Provider name</Table.Cell>
              <Table.Cell>{photoEditingJob.provider.name}</Table.Cell>
            </Table.Row>
          ) : null}
          <Table.Row key={3}>
            <Table.Cell>Item</Table.Cell>
            <Table.Cell>{photoEditingJob.typeLabel}</Table.Cell>
          </Table.Row>
          {/* For now we won't show the status when the job is in creation since it's too obvious */}
          {photoEditingJob.isStatusInCreation ? (
            <Table.Row key={4}>
              <Table.Cell>Status</Table.Cell>
              <Table.Cell>{photoEditingJob.statusLabel}</Table.Cell>
            </Table.Row>
          ) : null}
          <Table.Row key={5}>
            <Table.Cell>Images</Table.Cell>
            <Table.Cell>{photoEditingJob.photoEditingImages.length}</Table.Cell>
          </Table.Row>
          <Table.Row key={6}>
            <Table.Cell>ETA</Table.Cell>
            <Table.Cell>
              <EstimatedTime photoEditingJob={photoEditingJob} />
            </Table.Cell>
          </Table.Row>
          {photoEditingJob.isStatusInProgress && photoEditingJob.estimatedTime ? (
            <Table.Row key={7}>
              <Table.Cell>Time left to ETA</Table.Cell>
              <Table.Cell>{photoEditingJob.estimatedTimeLeftToFinishLabel}</Table.Cell>
            </Table.Row>
          ) : null}
          {photoEditingJob.startedDts ? (
            <Table.Row key={8}>
              <Table.Cell>Started</Table.Cell>
              <Table.Cell>{Moment(photoEditingJob.startedDts).format("MM/DD/YYYY HH:mm")}</Table.Cell>
            </Table.Row>
          ) : null}
          {photoEditingJob.finishedDts ? (
            <Table.Row key={9}>
              <Table.Cell>Finished</Table.Cell>
              <Table.Cell>{Moment(photoEditingJob.finished).format("MM/DD/YYYY HH:mm")}</Table.Cell>
            </Table.Row>
          ) : null}
          {photoEditingJob.isStatusDone ? (
            <Table.Row key={10}>
              <Table.Cell>Took</Table.Cell>
              <Table.Cell>{photoEditingJob.timeTookToFinish}</Table.Cell>
            </Table.Row>
          ) : null}
          {productBreakDown}
          <Table.Row key={tableRowKeyId}>
            <Table.Cell>Total</Table.Cell>
            <Table.Cell>{this.getCreditCostText(totalCost)}</Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
    );
  }
}

JobSummary.propTypes = {
  photoEditingJob: PropTypes.instanceOf(PhotoEditingJob),
  costPerItem: PropTypes.number.isRequired,
  totalCost: PropTypes.number.isRequired,
  showProviderInfo: PropTypes.bool,
};

JobSummary.defaultProps = {
  showProviderInfo: false,
};

export default JobSummary;
